import ImageUploader from './image-uploader';
import { createNanoEvents, Unsubscribe } from 'nanoevents';
import ImageMetaExtractor, { ImageMeta } from './image-meta-extractor';
import mutex from './mutex';
import ImageUrl from './image-url';
import { blobToBase64 } from '@src/slide/infrastructure/module/dependent/image/upload';

export enum EventTypes {
    WillUpload = 'will-upload',
    DidUpload = 'did-upload',
    UploadError = 'upload-error',
}

export interface EventHandlers {
    [EventTypes.WillUpload]: (resource: ImageResource) => void;
    [EventTypes.DidUpload]: (resource: ImageResource) => void;
    [EventTypes.UploadError]: (err: Error) => void;
}

export class ImageResource {
    public id: string;
    private blob: Blob;
    private meta?: ImageMeta;
    private quickLookUrl: string;
    private cdnUrl: string;
    private base64Url: string;
    private eventBus = createNanoEvents<EventHandlers>();
    private uploadMutex = mutex<ImageResource>();
    private lastUploadError?: Error & { isNetworkError?: boolean };

    public constructor(id: string, blob: Blob) {
        this.id = id;
        this.blob = blob;
        this.quickLookUrl = URL.createObjectURL(blob);
        this.cdnUrl = '';
    }

    public upload = (): Promise<ImageResource> => this.uploadMutex.lock(async () => {
        if (this.cdnUrl) {
            return this;
        }

        this.lastUploadError = undefined;

        try {
            this.eventBus.emit(EventTypes.WillUpload, this);
            const { url } = await ImageUploader.uploadBlob(this.blob);
            this.cdnUrl = url;
            this.eventBus.emit(EventTypes.DidUpload, this);
        } catch (err) {
            this.lastUploadError = err;
            this.eventBus.emit(EventTypes.UploadError, err);
            throw err;
        }

        return this;
    });

    public getSize(): number {
        return this.blob.size;
    }

    public async loadBase64Url(): Promise<string> {
        this.base64Url = await blobToBase64(this.blob);
        return this.base64Url;
    }

    public clearBase64Url(): void {
        this.base64Url = '';
    }

    public async loadMeta(): Promise<ImageMeta> {
        if (!this.meta) {
            this.meta = await ImageMetaExtractor.extractBlob(this.blob);
        }

        return this.meta;
    }

    public async tryLoadMeta(): Promise<void> {
        await this.loadMeta().catch(() => {
            /* Ignore Errors */
        });
    }

    public getQuickLookUrl(): string {
        if (!this.meta) return this.quickLookUrl;

        const { width, height } = this.meta;
        return ImageUrl.attachImageSizeToUrl(this.quickLookUrl, { width, height });
    }

    public getRawQuickLockUrl(): string {
        return this.quickLookUrl;
    }

    public getCdnUrl(): string {
        return this.cdnUrl;
    }

    public getBase64Url(): string {
        return this.base64Url;
    }

    public getBlob(): Blob {
        return this.blob;
    }

    public getLastUploadError(): (Error & { isNetworkError?: boolean; }) | undefined {
        return this.lastUploadError;
    }

    public onWillUpload(handler: EventHandlers[EventTypes.WillUpload]): Unsubscribe {
        return this.eventBus.on(EventTypes.WillUpload, handler);
    }

    public onDidUpload(handler: EventHandlers[EventTypes.DidUpload]): Unsubscribe {
        return this.eventBus.on(EventTypes.DidUpload, handler);
    }

    public onUploadError(handler: EventHandlers[EventTypes.UploadError]): Unsubscribe {
        return this.eventBus.on(EventTypes.UploadError, handler);
    }

    public getVerticalDpi(): number | undefined {
        return this.meta?.verticalDpi;
    }
}
